import { useEffect, useRef, useState } from "react";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export type TabVariant = "underline" | "pill" | "card";

export interface TabItem {
  label: string;
  value: string;
  count?: number;
  disabled?: boolean;
}

export interface TabsProps {
  tabs: TabItem[];
  activeTab: string;
  onChange: (value: string) => void;
  variant?: TabVariant;
  className?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function Tabs({
  tabs,
  activeTab,
  onChange,
  variant = "underline",
  className,
}: TabsProps) {
  const listRef  = useRef<HTMLDivElement>(null);
  const tabRefs  = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Sliding indicator position (underline variant)
  const [indicator, setIndicator] = useState({ left: 0, width: 0 });

  useEffect(() => {
    const el  = tabRefs.current.get(activeTab);
    const list = listRef.current;
    if (!el || !list) return;
    const listRect = list.getBoundingClientRect();
    const tabRect  = el.getBoundingClientRect();
    setIndicator({
      left:  tabRect.left - listRect.left,
      width: tabRect.width,
    });
  }, [activeTab, tabs]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    const enabledIdxs = tabs
      .map((t, i) => ({ t, i }))
      .filter(({ t }) => !t.disabled)
      .map(({ i }) => i);

    const pos = enabledIdxs.indexOf(idx);
    let nextPos = pos;

    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      nextPos = (pos + 1) % enabledIdxs.length;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      nextPos = (pos - 1 + enabledIdxs.length) % enabledIdxs.length;
    } else if (e.key === "Home") {
      e.preventDefault();
      nextPos = 0;
    } else if (e.key === "End") {
      e.preventDefault();
      nextPos = enabledIdxs.length - 1;
    } else {
      return;
    }

    const nextIdx = enabledIdxs[nextPos];
    onChange(tabs[nextIdx].value);
    tabRefs.current.get(tabs[nextIdx].value)?.focus();
  };

  // ── Underline variant ────────────────────────────────────────────────────

  if (variant === "underline") {
    return (
      <div className={cn("relative", className)}>
        <div
          ref={listRef}
          role="tablist"
          className="relative flex border-b border-border-default"
        >
          {tabs.map((tab, idx) => (
            <button
              key={tab.value}
              ref={(el) => {
                if (el) tabRefs.current.set(tab.value, el);
                else tabRefs.current.delete(tab.value);
              }}
              role="tab"
              aria-selected={activeTab === tab.value}
              aria-disabled={tab.disabled}
              tabIndex={activeTab === tab.value ? 0 : -1}
              disabled={tab.disabled}
              onClick={() => !tab.disabled && onChange(tab.value)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              className={cn(
                "relative px-4 py-3 text-sm font-medium transition-colors duration-150",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-inset",
                "disabled:opacity-40 disabled:cursor-not-allowed",
                activeTab === tab.value
                  ? "text-brand-purple"
                  : "text-content-secondary hover:text-content-primary",
              )}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={cn(
                  "ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full",
                  activeTab === tab.value
                    ? "bg-violet-100 dark:bg-violet-500/20 text-brand-purple dark:text-violet-300"
                    : "bg-surface-overlay text-content-tertiary",
                )}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}

          {/* Animated underline */}
          <span
            aria-hidden="true"
            className="absolute bottom-0 h-0.5 rounded-full bg-brand-purple transition-all duration-200 ease-out"
            style={{ left: indicator.left, width: indicator.width }}
          />
        </div>
      </div>
    );
  }

  // ── Pill variant ─────────────────────────────────────────────────────────

  if (variant === "pill") {
    return (
      <div
        ref={listRef}
        role="tablist"
        className={cn(
          "inline-flex gap-1 p-1 rounded-xl",
          "bg-surface-raised border border-border-default",
          className,
        )}
      >
        {tabs.map((tab, idx) => (
          <button
            key={tab.value}
            ref={(el) => {
              if (el) tabRefs.current.set(tab.value, el);
              else tabRefs.current.delete(tab.value);
            }}
            role="tab"
            aria-selected={activeTab === tab.value}
            aria-disabled={tab.disabled}
            tabIndex={activeTab === tab.value ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => !tab.disabled && onChange(tab.value)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-offset-1",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              activeTab === tab.value
                ? "bg-brand-purple text-white shadow-sm"
                : "text-content-secondary hover:text-content-primary hover:bg-surface-overlay",
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={cn(
                "ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full",
                activeTab === tab.value
                  ? "bg-white/20 text-white"
                  : "bg-surface-overlay text-content-tertiary",
              )}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  }

  // ── Card (folder tab) variant ─────────────────────────────────────────────

  return (
    <div className={cn(className)}>
      <div
        ref={listRef}
        role="tablist"
        className="flex border-b border-border-default"
      >
        {tabs.map((tab, idx) => (
          <button
            key={tab.value}
            ref={(el) => {
              if (el) tabRefs.current.set(tab.value, el);
              else tabRefs.current.delete(tab.value);
            }}
            role="tab"
            aria-selected={activeTab === tab.value}
            aria-disabled={tab.disabled}
            tabIndex={activeTab === tab.value ? 0 : -1}
            disabled={tab.disabled}
            onClick={() => !tab.disabled && onChange(tab.value)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className={cn(
              "relative px-5 py-2.5 text-sm font-medium transition-colors duration-150",
              "rounded-t-lg border border-b-0",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple focus-visible:ring-inset",
              "disabled:opacity-40 disabled:cursor-not-allowed",
              activeTab === tab.value
                ? [
                    "bg-white dark:bg-surface-raised",
                    "border-border-default",
                    "text-content-primary",
                    "-mb-px z-10",
                  ]
                : [
                    "bg-surface dark:bg-surface",
                    "border-transparent hover:border-border-default",
                    "text-content-secondary hover:text-content-primary",
                  ],
            )}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className={cn(
                "ml-2 text-xs font-semibold px-1.5 py-0.5 rounded-full",
                activeTab === tab.value
                  ? "bg-violet-100 dark:bg-violet-500/20 text-brand-purple dark:text-violet-300"
                  : "bg-surface-overlay text-content-tertiary",
              )}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

Tabs.displayName = "Tabs";
